import {
  RATE_MAX_PER_SECOND,
  RATE_MAX_PER_MINUTE,
  RATE_MAX_RETRIES,
} from "@/constants";
import { Sentry } from "@/lib/sentry";

const DEFAULT_BASE = "https://api.openf1.org/v1";

const BASE =
  (import.meta.env.VITE_OPENF1_API_BASE as string | undefined) ?? DEFAULT_BASE;

// Optional bearer token. OpenF1's historical tier is documented as free/no-auth,
// but if the API starts gating requests (401/403) you can supply a token via the
// VITE_OPENF1_API_KEY env var (.env.local) and it'll be sent on every request.
const API_KEY = import.meta.env.VITE_OPENF1_API_KEY as string | undefined;

// Carries the HTTP status so callers can distinguish auth failures (401/403),
// which are not retryable, from transient errors (429/5xx).
export class OpenF1Error extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
  ) {
    super(`OpenF1 ${path}: ${status}`);
    this.name = "OpenF1Error";
  }
}

interface OpenF1ErrorPayload {
  detail?: string;
}

type QueryParams = Record<string, string | number | boolean>;

// Identical concurrent GETs (same URL + auth header) share one network request.
const inFlightJsonRequests = new Map<string, Promise<unknown[]>>();

function logApiWarning(message: string, extra: Record<string, unknown>) {
  Sentry.logger.warn(message, {
    log_source: "openf1_api",
    ...extra,
  });
}

function logApiError(message: string, extra: Record<string, unknown>) {
  Sentry.logger.error(message, {
    log_source: "openf1_api",
    ...extra,
  });
}

export function isAuthError(err: unknown): err is OpenF1Error {
  return (
    err instanceof OpenF1Error && (err.status === 401 || err.status === 403)
  );
}

// ── Rate limiter ──────────────────────────────────────────────────────────────
// OpenF1's free tier allows 3 req/s and 30 req/min, enforced with HTTP 429.
// A naive concurrency cap is NOT enough: at high playback speed the chunk
// prefetcher can fire many requests in well under a second. We gate request
// *starts* with two sliding windows (per-second and per-minute) and, on 429,
// honour Retry-After with exponential backoff.

const startTimes: number[] = []; // ms timestamps of recent request starts
const waiters: Array<(startedAt: number) => void> = [];

// When any request receives a 429 we stamp a global block so all queued
// requests wait out the full backoff rather than firing immediately.
let blockedUntil = 0;

/** Pause all pending requests for at least `ms` milliseconds. */
function blockRateLimiter(ms: number) {
  blockedUntil = Math.max(blockedUntil, Date.now() + ms);
  schedulePump(ms);
}

function pruneOlderThan(cutoff: number) {
  // startTimes is append-only and sorted, so drop from the front.
  while (startTimes.length > 0 && startTimes[0] < cutoff) startTimes.shift();
}

// How long until a new request may start without breaching either window.
// Returns 0 if a slot is available right now.
function msUntilSlot(now: number): number {
  // Respect a global block set by a 429 response.
  const blockRemaining = blockedUntil - now;
  if (blockRemaining > 0) return blockRemaining;

  pruneOlderThan(now - 60_000);
  const inLastSecond = startTimes.filter((t) => t > now - 1_000).length;
  const inLastMinute = startTimes.length;

  let wait = 0;
  if (inLastSecond >= RATE_MAX_PER_SECOND) {
    const oldestInSecond = startTimes[startTimes.length - RATE_MAX_PER_SECOND];
    wait = Math.max(wait, oldestInSecond + 1_000 - now);
  }
  if (inLastMinute >= RATE_MAX_PER_MINUTE) {
    wait = Math.max(wait, startTimes[0] + 60_000 - now);
  }
  return wait;
}

let pumpTimer: ReturnType<typeof setTimeout> | null = null;
let pumpDueAt = 0;

function pump() {
  pumpTimer = null;
  if (waiters.length === 0) return;

  const now = Date.now();
  const wait = msUntilSlot(now);
  if (wait <= 0) {
    startTimes.push(now);
    const run = waiters.shift()!;
    run(now);
    // Try to release more in the same tick if windows still allow it.
    schedulePump(0);
  } else {
    schedulePump(wait);
  }
}

function schedulePump(delay: number) {
  const wait = Math.max(0, delay);
  const dueAt = Date.now() + wait;
  if (pumpTimer !== null) {
    if (pumpDueAt <= dueAt) return;
    clearTimeout(pumpTimer);
  }
  // Cached responses can free a slot before an existing rate-window timer fires.
  pumpDueAt = dueAt;
  pumpTimer = setTimeout(pump, wait);
}

// Resolves (with the slot's start timestamp) when the caller is cleared to
// start a request.
function acquireSlot(): Promise<number> {
  return new Promise((resolve) => {
    waiters.push(resolve);
    schedulePump(0);
  });
}

// Responses served from the f1-replay proxy cache (`X-Cache: EDGE | KV`) never
// reach OpenF1, so they give their slot back instead of counting against the
// OpenF1 windows. Direct OpenF1 responses carry no such header.
function releaseSlotIfCached(res: Response, startedAt: number) {
  const cache = res.headers?.get("X-Cache");
  if (cache !== "EDGE" && cache !== "KV") return;
  const idx = startTimes.indexOf(startedAt);
  if (idx === -1) return;
  startTimes.splice(idx, 1);
  schedulePump(0);
}

// ── Fetch with backoff ──────────────────────────────────────────────────────--
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function parseRetryAfter(res: Response): number | null {
  const h = res.headers.get("Retry-After");
  if (!h) return null;
  const secs = Number(h);
  if (!Number.isNaN(secs)) return secs * 1000;
  const dateMs = Date.parse(h);
  return Number.isNaN(dateMs) ? null : Math.max(0, dateMs - Date.now());
}

async function isNoResults404(path: string, res: Response): Promise<boolean> {
  if (res.status !== 404) return false;

  // Some OpenF1 endpoints legitimately return 404 for "no data" in a given
  // session/filter. Treat these as empty datasets instead of hard failures.
  if (path === "location" || path === "starting_grid") return true;

  try {
    const body = (await res.clone().json()) as OpenF1ErrorPayload;
    const detail = (body.detail ?? "").toLowerCase();
    return detail.includes("no results found") || detail.includes("no data");
  } catch {
    return false;
  }
}

export async function fetchEndpoint<T>(
  path: string,
  params: QueryParams,
): Promise<T[]> {
  // Build query string manually so comparison operators (>, <, >=, <=) in keys
  // are NOT percent-encoded — OpenF1 expects raw `date>value` syntax.
  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  const query = qs ? `?${qs}` : "";
  const url = `${BASE}/${path}${query}`;

  const headers: Record<string, string> = {};
  if (API_KEY) headers.Authorization = `Bearer ${API_KEY}`;

  const requestKey = `${url}|${headers.Authorization ?? ""}`;
  const existing = inFlightJsonRequests.get(requestKey) as
    | Promise<T[]>
    | undefined;
  if (existing) return existing;

  const requestPromise = (async () => {
    let attempt = 0;
    let lastError: Error | null = null;

    for (;;) {
      try {
        const queuedAt = globalThis.performance?.now() ?? Date.now();
        const slotStartedAt = await acquireSlot();
        const requestStartedAt = globalThis.performance?.now() ?? Date.now();
        Sentry.metrics.distribution(
          "request_queue_time",
          Math.round(requestStartedAt - queuedAt),
          { unit: "millisecond", attributes: { endpoint: path } },
        );
        const res = await fetch(url, { headers });
        releaseSlotIfCached(res, slotStartedAt);
        const responseReceivedAt = globalThis.performance?.now() ?? Date.now();
        const responseTimeMs = Math.round(responseReceivedAt - requestStartedAt);
        Sentry.metrics.distribution("response_time", responseTimeMs);

        if (res.ok) {
          try {
            const data = await res.json();
            Sentry.metrics.distribution(
              "response_body_time",
              Math.round(
                (globalThis.performance?.now() ?? Date.now()) -
                responseReceivedAt,
              ),
              { unit: "millisecond", attributes: { endpoint: path } },
            );
            if (!Array.isArray(data)) {
              logApiWarning("OpenF1 returned non-array JSON payload", {
                path,
                url,
                payload_type: typeof data,
              });
              console.warn(
                `Expected array response from ${path}, got:`,
                typeof data,
              );
              return [];
            }
            return data;
          } catch (parseErr) {
            logApiError("Failed to parse OpenF1 JSON response", {
              path,
              url,
              attempt,
            });
            Sentry.captureException(
              parseErr instanceof Error
                ? parseErr
                : new Error(String(parseErr)),
              {
                tags: { log_source: "openf1_api", failure_type: "parse" },
                extra: { path, url, attempt },
              },
            );
            console.error("Failed to parse JSON response from", path, parseErr);
            throw new OpenF1Error(0, `${path} (parse error)`);
          }
        }

        // OpenF1 returns 404 + `{"detail":"No results found."}` for empty filters.
        // Treat that specific case as an empty dataset.
        if (await isNoResults404(path, res)) return [];

        // Retry on 429 (rate limit) and 5xx (transient server) with backoff.
        // Auth (401/403) and other 4xx are terminal — fail fast.
        const retryable = res.status === 429 || res.status >= 500;
        if (!retryable || attempt >= RATE_MAX_RETRIES) {
          logApiError("OpenF1 request failed", {
            path,
            url,
            status: res.status,
            attempt,
            retryable,
          });
          const err = new OpenF1Error(res.status, path);
          lastError = err;
          Sentry.captureException(err, {
            tags: { log_source: "openf1_api", failure_type: "http" },
            extra: { path, url, status: res.status, attempt, retryable },
          });
          throw err;
        }

        const backoff = parseRetryAfter(res) ?? 2 ** attempt * 1_000; // 1s,2s,4s,8s…
        // Block all other queued requests too — they'll hit the same 429 otherwise.
        blockRateLimiter(backoff);
        logApiWarning("Retrying OpenF1 request after retryable response", {
          path,
          url,
          status: res.status,
          attempt: attempt + 1,
          backoff_ms: backoff,
        });
        attempt++;
        await sleep(backoff);
      } catch (err) {
        // Network errors, parsing errors, etc.
        if (err instanceof OpenF1Error) throw err;
        lastError = err instanceof Error ? err : new Error(String(err));

        if (attempt >= RATE_MAX_RETRIES) {
          logApiError("OpenF1 request exhausted network retries", {
            path,
            url,
            attempt,
            error_message: lastError.message,
          });
          Sentry.captureException(lastError, {
            tags: { log_source: "openf1_api", failure_type: "network" },
            extra: { path, url, attempt },
          });
          console.error(
            `fetchEndpoint: max retries exceeded for ${path}:`,
            lastError,
          );
          throw new OpenF1Error(0, `${path} (network error)`);
        }

        attempt++;
        const backoff = 2 ** attempt * 500;
        logApiWarning("Retrying OpenF1 request after network error", {
          path,
          url,
          attempt,
          backoff_ms: backoff,
          error_message: lastError.message,
        });
        await sleep(backoff);
      }
    }
  })();

  inFlightJsonRequests.set(requestKey, requestPromise as Promise<unknown[]>);
  try {
    return await requestPromise;
  } finally {
    inFlightJsonRequests.delete(requestKey);
  }
}

export async function downloadEndpointCsv(
  path: string,
  params: QueryParams,
  fileName: string,
): Promise<boolean> {
  const qs = Object.entries({ ...params, csv: true })
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  const query = qs ? `?${qs}` : "";
  const url = `${BASE}/${path}${query}`;

  const headers: Record<string, string> = {};
  if (API_KEY) headers.Authorization = `Bearer ${API_KEY}`;

  let attempt = 0;
  for (;;) {
    const slotStartedAt = await acquireSlot();
    const requestStartedAt = globalThis.performance?.now() ?? Date.now();
    const res = await fetch(url, { headers });
    releaseSlotIfCached(res, slotStartedAt);
    const responseTimeMs = Math.round(
      (globalThis.performance?.now() ?? Date.now()) - requestStartedAt,
    );
    Sentry.metrics.distribution("response_time", responseTimeMs);

    if (res.ok) {
      const csv = await res.text();
      if (!csv.trim()) return false;

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      return true;
    }

    if (await isNoResults404(path, res)) return false;

    const retryable = res.status === 429 || res.status >= 500;
    if (!retryable || attempt >= RATE_MAX_RETRIES) {
      const err = new OpenF1Error(res.status, path);
      logApiError("OpenF1 CSV download failed", {
        path,
        url,
        status: res.status,
        attempt,
        retryable,
      });
      Sentry.captureException(err, {
        tags: { log_source: "openf1_api", failure_type: "csv_download" },
        extra: { path, url, status: res.status, attempt, retryable },
      });
      throw err;
    }

    const backoff = parseRetryAfter(res) ?? 2 ** attempt * 1_000;
    blockRateLimiter(backoff);
    logApiWarning("Retrying OpenF1 CSV download", {
      path,
      url,
      status: res.status,
      attempt: attempt + 1,
      backoff_ms: backoff,
    });
    attempt++;
    await sleep(backoff);
  }
}
